// First on purpose: pins the cascade-layer order for everything that follows.
// A layer ranks where it is first declared, and Tailwind's layers and the
// shared kit's `@layer bla` both appear later in this graph -- see layers.css.
import './styles/layers.css'

import { StrictMode } from 'react'

import { createRoot } from 'react-dom/client'

import { AuthProvider } from '@blattnercompany/react-auth'

import App from './App'
import { AuthError, AuthLoading } from './components/AuthStatus'
import { apiScopes, msalConfig } from './configs/authConfig'
import { logger } from './utils/logger'

import './styles/tokens.css'
// The shared UI kit's theme and component CSS, all inside `@layer bla`. Its
// rank is pinned by layers.css above, so its position here is not
// load-bearing -- it sits with the other stylesheets for legibility.
import '@blattnercompany/ui/style.css'
// After tokens.css: the mapping reads those variables. Before index.css so a
// library component's variables are resolvable wherever it mounts.
import './styles/blattner-ui-tokens.css'
import './styles/index.css'
import './styles/components.css'

const log = logger.scoped('global')

// Catch what React can't — errors outside the component tree and unhandled
// promise rejections (React_Web_Standards §12).
window.addEventListener('error', (event) => {
  log.error('Unhandled error', { error: event.error })
})
window.addEventListener('unhandledrejection', (event) => {
  log.error('Unhandled rejection', { error: event.reason })
})

// Microsoft Entra ID SSO comes from @blattnercompany/react-auth: <AuthProvider>
// builds the PublicClientApplication, publishes it to the package's shared slot
// (which is how apiClient's interceptor reaches it), and auto-redirects
// unauthenticated visits to Entra — no in-app login screen.
//
// Mount exactly one AuthProvider, here at the root — the package's shared MSAL
// slot is last-writer-wins.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider
      config={msalConfig}
      scopes={apiScopes}
      loadingComponent={AuthLoading}
      errorComponent={AuthError}
    >
      <App />
    </AuthProvider>
  </StrictMode>,
)
